import { CustomFields, Entity, GPSTrack, HexColor, Location } from '@nx-smartmonkey/shared/domain';
import { removeEmpty } from '@nx-smartmonkey/shared/helpers';
import { TimeWindow } from '@nx-smartmonkey/shared/interfaces';

import { kmToSM } from '../../helpers/Units';
import configuration from '../../services/configuration';
import { DriversSurvey } from '../driversSurvey/DriversSurvey';
import { Project } from '../project/Project';
import { Stop } from '../stop/Stop';
import { FuelTypes } from '../vehicle/Vehicle';
import { DriverStatus } from './DriverStatus';

export interface DriverVehicleProps {
  fuel_type?: FuelTypes;
  consumption?: number;
}

export interface DriverUpdateProps {
  vehicle_id?: string;
  external_id?: string;
  start_location?: Location | null;
  end_location?: Location | null;
  label?: string;
  phone?: string;
  comments?: string;
  email?: string;
  plate?: string;
  vehicle_model?: string;
  color?: HexColor;
  brand?: string;
  is_locked?: boolean;

  // Constraints
  geo_fences?: Array<string>;
  time_window?: [number, number];
  max_distance?: number;
  min_distance?: number;
  max_time?: number;
  min_time?: number;
  max_weight?: number;
  max_volume?: number;
  max_services?: number;
  provides?: Array<string>;
  custom_fields?: CustomFields;
  price_per_distance?: number;
  price_per_minute?: number;

  // Status,
  status?: DriverStatus;
}

export interface DriverProps {
  plan_id?: string;
  project_id: string;
  external_id?: string;
  vehicle_id?: string;
  vehicle?: DriverVehicleProps;
  label?: string;
  phone?: string;
  comments?: string;
  email?: string;
  plate?: string;
  color?: HexColor;
  vehicle_model?: string;
  brand?: string;
  time_window?: TimeWindow;
  geo_fences?: string[];
  max_distance?: number;
  min_distance?: number;
  max_time?: number;
  min_time?: number;
  max_weight?: number;
  max_volume?: number;
  max_services?: number;
  start_location?: Location | null;
  end_location?: Location | null;
  provides?: string[];
  start_date?: Date;
  end_date?: Date;
  estimated_end_time?: Date;
  status: DriverStatus;
  is_locked?: boolean;
  planned_track?: string;
  planned_start_time?: number;
  planned_end_time?: number;
  total_distance?: number;
  distance_traveled?: number;
  last_position?: GPSTrack;
  price_per_distance?: number;
  price_per_minute?: number;
  toll_price?: number;
  toll_price_currency?: string;
  tracing_percent?: number;
  custom_fields?: CustomFields;
  stops: Stop[];
  survey?: DriversSurvey;
  deleted?: boolean;
  created_by?: string;
  created_at?: Date;
  updated_at?: Date;
  deleted_at?: Date;
}

export class Driver extends Entity<DriverProps> {
  get id(): string {
    return this._id!;
  }
  get plan_id(): string | undefined {
    return this.props.plan_id;
  }
  set plan_id(plan_id: string | undefined) {
    this.props.plan_id = plan_id;
  }
  get project_id(): string {
    return this.props.project_id;
  }
  set project_id(project_id: string) {
    this.props.project_id = project_id;
  }
  get external_id(): string | undefined {
    return this.props.external_id;
  }
  set external_id(external_id: string | undefined) {
    this.props.external_id = external_id;
  }
  get vehicle_id(): string | undefined {
    return this.props.vehicle_id;
  }
  set vehicle_id(vehicle_id: string | undefined) {
    this.props.vehicle_id = vehicle_id;
  }
  get vehicle(): DriverVehicleProps | undefined {
    return this.props.vehicle;
  }
  set vehicle(vehicle: DriverVehicleProps | undefined) {
    this.props.vehicle = vehicle;
  }
  get fuel_type(): FuelTypes | undefined {
    return this.props.vehicle?.fuel_type;
  }
  set fuel_type(fuel_type: FuelTypes | undefined) {
    this.props.vehicle ? (this.props.vehicle.fuel_type = fuel_type) : (this.props.vehicle = { fuel_type });
  }
  get consumption(): number | undefined {
    return this.props.vehicle?.consumption;
  }
  set consumption(consumption: number | undefined) {
    this.props.vehicle ? (this.props.vehicle.consumption = consumption) : (this.props.vehicle = { consumption });
  }
  get label(): string | undefined {
    return this.props.label;
  }
  set label(label: string | undefined) {
    this.props.label = label;
  }
  get phone(): string | undefined {
    return this.props.phone;
  }
  set phone(phone: string | undefined) {
    this.props.phone = phone;
  }
  get comments(): string | undefined {
    return this.props.comments;
  }
  set comments(comments: string | undefined) {
    this.props.comments = comments;
  }
  get email(): string | undefined {
    return this.props.email;
  }
  set email(email: string | undefined) {
    this.props.email = email;
  }
  get plate(): string | undefined {
    return this.props.plate;
  }
  set plate(plate: string | undefined) {
    this.props.plate = plate;
  }
  get color(): HexColor | undefined {
    return this.props.color;
  }
  set color(color: HexColor | undefined) {
    this.props.color = color;
  }
  get vehicle_model(): string | undefined {
    return this.props.vehicle_model;
  }
  set vehicle_model(vehicle_model: string | undefined) {
    this.props.vehicle_model = vehicle_model;
  }
  get brand(): string | undefined {
    return this.props.brand;
  }
  set brand(brand: string | undefined) {
    this.props.brand = brand;
  }
  get time_window(): TimeWindow | undefined {
    return this.props.time_window;
  }
  set time_window(time_window: TimeWindow | undefined) {
    this.props.time_window = time_window;
  }
  get geo_fences(): string[] | undefined {
    return this.props.geo_fences;
  }
  set geo_fences(geo_fences: string[] | undefined) {
    this.props.geo_fences = geo_fences;
  }
  get max_distance(): number | undefined {
    return this.props.max_distance;
  }
  set max_distance(max_distance: number | undefined) {
    this.props.max_distance = max_distance;
  }
  get min_distance(): number | undefined {
    return this.props.min_distance;
  }
  set min_distance(min_distance: number | undefined) {
    this.props.min_distance = min_distance;
  }
  get max_time(): number | undefined {
    return this.props.max_time;
  }
  set max_time(max_time: number | undefined) {
    this.props.max_time = max_time;
  }
  get min_time(): number | undefined {
    return this.props.min_time;
  }
  set min_time(min_time: number | undefined) {
    this.props.min_time = min_time;
  }
  get max_weight(): number | undefined {
    return this.props.max_weight;
  }
  set max_weight(max_weight: number | undefined) {
    this.props.max_weight = max_weight;
  }
  get max_volume(): number | undefined {
    return this.props.max_volume;
  }
  set max_volume(max_volume: number | undefined) {
    this.props.max_volume = max_volume;
  }
  get max_services(): number | undefined {
    return this.props.max_services;
  }
  set max_services(max_services: number | undefined) {
    this.props.max_services = max_services;
  }
  get start_location(): Location | undefined | null {
    return this.props.start_location;
  }
  set start_location(start_location: Location | undefined | null) {
    this.props.start_location = start_location;
  }
  get end_location(): Location | undefined | null {
    return this.props.end_location;
  }
  set end_location(end_location: Location | undefined | null) {
    this.props.end_location = end_location;
  }
  get provides(): string[] | undefined {
    return this.props.provides;
  }
  set provides(provides: string[] | undefined) {
    this.props.provides = provides;
  }
  get start_date(): Date | undefined {
    return this.props.start_date;
  }
  set start_date(start_date: Date | undefined) {
    this.props.start_date = start_date;
  }
  get end_date(): Date | undefined {
    return this.props.end_date;
  }
  set end_date(end_date: Date | undefined) {
    this.props.end_date = end_date;
  }
  get estimated_end_time(): Date | undefined {
    return this.props.estimated_end_time;
  }
  get status(): DriverStatus {
    return this.props.status;
  }
  set status(status: DriverStatus) {
    this.props.status = status;
  }
  get is_locked(): boolean | undefined {
    return this.props.is_locked;
  }
  set is_locked(is_locked: boolean | undefined) {
    this.props.is_locked = is_locked;
  }
  get planned_track(): string | undefined {
    return this.props.planned_track;
  }
  set planned_track(planned_track: string | undefined) {
    this.props.planned_track = planned_track;
  }
  get planned_start_time(): number | undefined {
    return this.props.planned_start_time;
  }
  set planned_start_time(planned_start_time: number | undefined) {
    this.props.planned_start_time = planned_start_time;
  }
  get planned_end_time(): number | undefined {
    return this.props.planned_end_time;
  }
  set planned_end_time(planned_end_time: number | undefined) {
    this.props.planned_end_time = planned_end_time;
  }
  get total_distance(): number | undefined {
    return this.props.total_distance;
  }
  set total_distance(total_distance: number | undefined) {
    this.props.total_distance = total_distance;
  }
  get distance_traveled(): number | undefined {
    return this.props.distance_traveled;
  }
  set distance_traveled(distance_traveled: number | undefined) {
    this.props.distance_traveled = distance_traveled;
  }
  get last_position(): GPSTrack | undefined {
    return this.props.last_position;
  }
  set last_position(last_position: GPSTrack | undefined) {
    this.props.last_position = last_position;
  }
  get price_per_distance(): number | undefined {
    return this.props.price_per_distance;
  }
  set price_per_distance(price_per_distance: number | undefined) {
    this.props.price_per_distance = price_per_distance;
  }
  get price_per_minute(): number | undefined {
    return this.props.price_per_minute;
  }
  set price_per_minute(price_per_minute: number | undefined) {
    this.props.price_per_minute = price_per_minute;
  }
  get toll_price(): number | undefined {
    return this.props.toll_price;
  }
  set toll_price(toll_price: number | undefined) {
    this.props.toll_price = toll_price;
  }
  get toll_price_currency(): string | undefined {
    return this.props.toll_price_currency;
  }
  set toll_price_currency(toll_price_currency: string | undefined) {
    this.props.toll_price_currency = toll_price_currency;
  }
  get tracing_percent(): number | undefined {
    return this.props.tracing_percent;
  }
  set tracing_percent(tracing_percent: number | undefined) {
    this.props.tracing_percent = tracing_percent;
  }
  get custom_fields(): CustomFields | undefined {
    return this.props.custom_fields;
  }
  set custom_fields(custom_fields: CustomFields | undefined) {
    this.props.custom_fields = custom_fields;
  }
  get stops(): Stop[] {
    return this.props.stops;
  }
  set stops(stops: Stop[]) {
    this.props.stops = stops;
  }
  get survey(): DriversSurvey | undefined {
    return this.props.survey;
  }
  set survey(survey: DriversSurvey | undefined) {
    this.props.survey = survey;
  }
  get deleted(): boolean | undefined {
    return this.props.deleted;
  }
  get created_by(): string | undefined {
    return this.props.created_by;
  }
  set created_by(created_by: string | undefined) {
    this.props.created_by = created_by;
  }
  get created_at(): Date | undefined {
    return this.props.created_at;
  }
  set created_at(created_at: Date | undefined) {
    this.props.created_at = created_at;
  }
  get updated_at(): Date | undefined {
    return this.props.updated_at;
  }
  set updated_at(updated_at: Date | undefined) {
    this.props.updated_at = updated_at;
  }
  get deleted_at(): Date | undefined {
    return this.props.deleted_at;
  }
  set deleted_at(deleted_at: Date | undefined) {
    this.props.deleted_at = deleted_at;
  }
  update(props: DriverUpdateProps) {
    removeEmpty(props);

    if (props.vehicle_id !== undefined) this.props.vehicle_id = props.vehicle_id;
    if (props.external_id !== undefined) this.props.external_id = props.external_id;
    if (props.start_location !== undefined) this.props.start_location = props.start_location;
    if (props.end_location !== undefined) this.props.end_location = props.end_location;
    if (props.label !== undefined) this.props.label = props.label;
    if (props.phone !== undefined) this.props.phone = props.phone;
    if (props.comments !== undefined) this.props.comments = props.comments;
    if (props.email !== undefined) this.props.email = props.email;
    if (props.plate !== undefined) this.props.plate = props.plate;
    if (props.vehicle_model !== undefined) this.props.vehicle_model = props.vehicle_model;
    if (props.color !== undefined) this.props.color = props.color;
    if (props.brand !== undefined) this.props.brand = props.brand;
    if (props.is_locked !== undefined) this.props.is_locked = props.is_locked;

    // Constraints
    if (props.geo_fences !== undefined) this.props.geo_fences = props.geo_fences;
    if (props.time_window !== undefined) this.props.time_window = props.time_window;
    if (props.max_distance !== undefined) this.props.max_distance = props.max_distance;
    if (props.min_distance !== undefined) this.props.min_distance = props.min_distance;
    if (props.max_time !== undefined) this.props.max_time = props.max_time;
    if (props.min_time !== undefined) this.props.min_time = props.min_time;
    if (props.max_weight !== undefined) this.props.max_weight = props.max_weight;
    if (props.max_volume !== undefined) this.props.max_volume = props.max_volume;
    if (props.max_services !== undefined) this.props.max_services = props.max_services;
    if (props.provides !== undefined) this.props.provides = props.provides;
    if (props.custom_fields !== undefined) this.props.custom_fields = props.custom_fields;
    if (props.price_per_distance !== undefined) this.props.price_per_distance = props.price_per_distance;
    if (props.price_per_minute !== undefined) this.props.price_per_minute = props.price_per_minute;
    // Status,
    if (props.status !== undefined) this.props.status = props.status!;

    this.props.updated_at = new Date();
  }
  private constructor(props: DriverProps, id?: string) {
    super(props, id);
  }
  static create(props: DriverProps, id?: string): Driver {
    return new Driver(props, id);
  }

  /**
   * Shorten driver label.
   * EXAMPLES:
   * - John Doe -> JD
   * - JohnDoe -> JO
   * - J -> J
   * @returns string
   */
  getShortLabel(): string {
    if (!this.label) return ``;
    let displayName: string = this.label.trim().replace(/\s{2,}/g, ` `);
    const displayNameSplit = displayName.split(` `);
    if (displayNameSplit.length > 1) {
      displayName = `${displayNameSplit[0][0]}${displayNameSplit[1][0]}`;
    } else if (displayNameSplit[0].length > 1) {
      displayName = `${displayNameSplit[0][0]}${displayNameSplit[0][1]}`;
    }
    return displayName.toLocaleUpperCase();
  }

  getRouteMagicLink(): string {
    return Driver.generateRouteMagicLink(this.id, this.vehicle_id ?? ``);
  }

  static generateRouteMagicLink(routeId: string, vehicleId: string, isOnboarding = false): string {
    const path = `magic_link?&vehicle_id=${encodeURIComponent(vehicleId)}&route_id=${encodeURIComponent(routeId)}${
      isOnboarding ? `&from_planner_onboarding=true` : ``
    }
    `;
    const publicRouteLink = `${configuration.DRIVERS_ENDPOINT}/${path}`;
    return publicRouteLink;
  }

  getAssignedVolume(): number {
    return this.stops.reduce((amount: number, stop: Stop) => {
      return amount + (stop.volume ?? 0);
    }, 0);
  }

  getAssignedWeight(): number {
    return this.stops.reduce((amount: number, stop: Stop) => {
      return amount + (stop.weight ?? 0);
    }, 0);
  }

  getTotalMinutes(): number {
    const totalMinutes =
      this.planned_end_time !== undefined && this.planned_start_time !== undefined
        ? this.planned_end_time / 60 - this.planned_start_time / 60
        : 0;
    return totalMinutes;
  }

  getTotalPricePerHour(): number {
    const minutePrice = this.price_per_minute || 0;
    const totalMinutes = this.getTotalMinutes();
    const totalPricePerHour = (totalMinutes * minutePrice) / 60;
    return totalPricePerHour;
  }

  getTotalPriceUsingKM(): number {
    // Price per minute calculation (hours for human readable)
    const totalPricePerHour = this.getTotalPricePerHour();

    // Get total distance
    const kmDistance = this.getTotalDistanceKM();

    // Total price calculation
    const distancePrice = this.price_per_distance || 0;
    const totalPricePerDistance = kmDistance * distancePrice;
    return totalPricePerDistance + totalPricePerHour;
  }

  getTotalPriceWithUnits(project: Project): string {
    // Price per minute calculation (hours for human readable)
    const totalPricePerHour = this.getTotalPricePerHour();

    // Price per distance calculation
    const kmDistance = this.getTotalDistanceKM();
    const totalDistance = kmToSM(kmDistance, project?.units?.distance);

    // Total price calculation
    const distancePrice = this.price_per_distance || 0;
    const totalPricePerDistance = totalDistance * distancePrice;
    return `${(totalPricePerDistance + totalPricePerHour).toFixed(2)} ${project.units?.currencyToSymbol()}`;
  }

  /**
   * Total distance is the amount of distance_to_next_stop of each
   * stop and the first route service distance_to_previous_stop.
   */
  getTotalDistanceKM(): number {
    if (this.stops.length === 0) return 0;

    const distanceToFirstService = this.stops[0].distance_to_previous_stop ?? 0;
    const totalDistance =
      (this.stops.reduce((ps: any, ns: any) => ps + (ns.distance_to_next_stop || 0), distanceToFirstService) || 0) /
      1000;

    return totalDistance;
  }

  /**
   * Total distance with units.
   */
  getTotalDistanceWithUnits(project: Project): string {
    const totalDistance = this.getTotalDistanceKM();
    return `${
      kmToSM(totalDistance, project?.units?.distance) > 100
        ? Math.floor(kmToSM(totalDistance, project?.units?.distance))
        : kmToSM(totalDistance, project?.units?.distance).toFixed(1)
    }${project?.units?.distance}`;
  }

  /**
   * Transform the toll currency to symbol.
   */
  tollCurrencyToSymbol(): string {
    const settingsCurrency = [
      { code: `EUR`, symbol: `€` },
      { code: `USD`, symbol: `$` },
      { code: `MXN`, symbol: `$` },
    ];
    if (this.toll_price_currency) {
      const currencyFound = settingsCurrency.find((c: any) => c.code === this.toll_price_currency);
      if (currencyFound) {
        return `${currencyFound.symbol}`;
      }
    }
    return ``;
  }
}
